import { Category, WeightedKeyword, CategoryConfig } from "types";

export const categories: Category[] = [
  {
    id: "1",
    name: "Study Abroad",
    slug: "study-abroad",
  },
  {
    id: "2",
    name: "Admissions",
    slug: "admissions",
  },
  {
    id: "3",
    name: "Student Guide",
    slug: "student-guide",
  },
  {
    id: "4",
    name: "Scholarships",
    slug: "scholarships",
  },
  {
    id: "5",
    name: "Visa Guide",
    slug: "visa-guide",
  },
  {
    id: "6",
    name: "Career",
    slug: "career",
  },
  {
    id: "7",
    name: "Student Stories",
    slug: "student-stories",
  },
];

export const createWeightedKeywords = (
  phrases: string[],
  type: "core" | "context" | "technical",
  baseWeight: number
): WeightedKeyword[] => {
  return phrases.map((phrase) => ({
    phrase,
    weight:
      baseWeight *
      (type === "core" ? 2.0 : type === "technical" ? 1.5 : 1.0) *
      (phrase.includes(" ") ? 1.5 : 1.0),
    type,
  }));
};

export const enhancedCategoryKeywords: Record<string, CategoryConfig> = {
  "study-abroad": {
    baseWeight: 2.0,
    requiresCoreMatch: true,
    keywords: [
      // Core terms - multi-word phrases
      { phrase: "study abroad", weight: 2.5, type: "core" },
      { phrase: "international students", weight: 2.5, type: "core" },
      { phrase: "study destinations", weight: 2.5, type: "core" },
      { phrase: "foreign university", weight: 2.5, type: "core" },
      { phrase: "overseas education", weight: 2.5, type: "core" },
      { phrase: "global education", weight: 2.5, type: "core" },
      { phrase: "international education", weight: 2.5, type: "core" },
      { phrase: "study overseas", weight: 2.5, type: "core" },
      { phrase: "studying abroad", weight: 2.5, type: "core" },
      { phrase: "study internationally", weight: 2.5, type: "core" },

      // Core terms - single words
      { phrase: "abroad", weight: 2.5, type: "core" },
      { phrase: "overseas", weight: 2.5, type: "core" },
      { phrase: "international", weight: 2.5, type: "core" },
      { phrase: "globally", weight: 2.5, type: "core" },
      { phrase: "worldwide", weight: 2.5, type: "core" },

      // Technical terms - multi-word
      { phrase: "education system", weight: 2.0, type: "technical" },
      { phrase: "world ranking", weight: 2.0, type: "technical" },
      { phrase: "university ranking", weight: 2.0, type: "technical" },
      { phrase: "global recognition", weight: 2.0, type: "technical" },
      { phrase: "international recognition", weight: 2.0, type: "technical" },
      { phrase: "foreign institution", weight: 2.0, type: "technical" },
      { phrase: "study program", weight: 2.0, type: "technical" },
      { phrase: "foreign degree", weight: 2.0, type: "technical" },
      { phrase: "international campus", weight: 2.0, type: "technical" },
      { phrase: "global university", weight: 2.0, type: "technical" },

      // Technical terms - single words
      { phrase: "destination", weight: 2.0, type: "technical" },
      { phrase: "university", weight: 2.0, type: "technical" },
      { phrase: "institution", weight: 2.0, type: "technical" },
      { phrase: "campus", weight: 2.0, type: "technical" },
      { phrase: "program", weight: 2.0, type: "technical" },

      // Context terms - multi-word
      { phrase: "cultural experience", weight: 1.5, type: "context" },
      { phrase: "student community", weight: 1.5, type: "context" },
      { phrase: "global exposure", weight: 1.5, type: "context" },
      { phrase: "diverse environment", weight: 1.5, type: "context" },
      { phrase: "student life abroad", weight: 1.5, type: "context" },
      { phrase: "cultural diversity", weight: 1.5, type: "context" },
      { phrase: "global perspective", weight: 1.5, type: "context" },
      { phrase: "cross cultural", weight: 1.5, type: "context" },
      { phrase: "student exchange", weight: 1.5, type: "context" },
      { phrase: "foreign culture", weight: 1.5, type: "context" },

      // Context terms - single words
      { phrase: "culture", weight: 1.5, type: "context" },
      { phrase: "diversity", weight: 1.5, type: "context" },
      { phrase: "multicultural", weight: 1.5, type: "context" },
      { phrase: "exchange", weight: 1.5, type: "context" },
      { phrase: "foreign", weight: 1.5, type: "context" },
    ],
  },

  admissions: {
    baseWeight: 2.0,
    requiresCoreMatch: true,
    keywords: [
      // Core terms - multi-word
      { phrase: "admission process", weight: 2.5, type: "core" },
      { phrase: "university admission", weight: 2.5, type: "core" },
      { phrase: "application process", weight: 2.5, type: "core" },
      { phrase: "college admission", weight: 2.5, type: "core" },
      { phrase: "admission requirements", weight: 2.5, type: "core" },
      { phrase: "entry requirements", weight: 2.5, type: "core" },
      { phrase: "admissions guide", weight: 2.5, type: "core" },
      { phrase: "how to apply", weight: 2.5, type: "core" },
      { phrase: "course application", weight: 2.5, type: "core" },
      { phrase: "program application", weight: 2.5, type: "core" },

      // Core terms - single words
      { phrase: "admission", weight: 2.5, type: "core" },
      { phrase: "admissions", weight: 2.5, type: "core" },
      { phrase: "apply", weight: 2.5, type: "core" },
      { phrase: "application", weight: 2.5, type: "core" },
      { phrase: "enroll", weight: 2.5, type: "core" },
      { phrase: "enrollment", weight: 2.5, type: "core" },

      // Technical terms - multi-word
      { phrase: "personal statement", weight: 2.0, type: "technical" },
      { phrase: "application documents", weight: 2.0, type: "technical" },
      { phrase: "recommendation letters", weight: 2.0, type: "technical" },
      { phrase: "academic transcripts", weight: 2.0, type: "technical" },
      { phrase: "admission tests", weight: 2.0, type: "technical" },
      { phrase: "academic records", weight: 2.0, type: "technical" },
      { phrase: "entrance exam", weight: 2.0, type: "technical" },
      { phrase: "reference letter", weight: 2.0, type: "technical" },
      { phrase: "admission essay", weight: 2.0, type: "technical" },
      { phrase: "statement of purpose", weight: 2.0, type: "technical" },
      {
        phrase: "admission academic requirements",
        weight: 2.0,
        type: "technical",
      },

      // Technical terms - single words
      { phrase: "transcript", weight: 2.0, type: "technical" },
      { phrase: "documents", weight: 2.0, type: "technical" },
      { phrase: "references", weight: 2.0, type: "technical" },
      { phrase: "portfolio", weight: 2.0, type: "technical" },
      { phrase: "prerequisites", weight: 2.0, type: "technical" },

      // Context terms - multi-word
      { phrase: "test scores", weight: 1.5, type: "context" },
      { phrase: "eligibility criteria", weight: 1.5, type: "context" },
      { phrase: "selection process", weight: 1.5, type: "context" },
      { phrase: "intake dates", weight: 1.5, type: "context" },
      { phrase: "application deadline", weight: 1.5, type: "context" },
      { phrase: "minimum requirements", weight: 1.5, type: "context" },
      { phrase: "admission cycle", weight: 1.5, type: "context" },
      { phrase: "intake period", weight: 1.5, type: "context" },
      { phrase: "entry criteria", weight: 1.5, type: "context" },

      // Context terms - single words
      { phrase: "deadline", weight: 1.5, type: "context" },
      { phrase: "requirements", weight: 1.5, type: "context" },
      { phrase: "eligible", weight: 1.5, type: "context" },
      { phrase: "criteria", weight: 1.5, type: "context" },
      { phrase: "qualifications", weight: 1.5, type: "context" },
    ],
  },

  "student-guide": {
    baseWeight: 2.0,
    requiresCoreMatch: true,
    keywords: [
      // Core terms - multi-word
      { phrase: "credit system", weight: 2.5, type: "core" },
      { phrase: "course registration", weight: 2.5, type: "core" },
      { phrase: "academic calendar", weight: 2.5, type: "core" },
      { phrase: "study hours", weight: 2.5, type: "core" },
      { phrase: "course selection", weight: 2.5, type: "core" },
      { phrase: "degree requirements", weight: 2.5, type: "core" },
      { phrase: "student handbook", weight: 2.5, type: "core" },
      { phrase: "study guide", weight: 2.5, type: "core" },
      { phrase: "campus guide", weight: 2.5, type: "core" },
      { phrase: "academic guide", weight: 2.5, type: "core" },
      {
        phrase: "institutional academic requirements",
        weight: 2.5,
        type: "core",
      },

      // Core terms - single words
      { phrase: "guide", weight: 2.5, type: "core" },
      { phrase: "handbook", weight: 2.5, type: "core" },
      { phrase: "manual", weight: 2.5, type: "core" },
      { phrase: "coursework", weight: 2.5, type: "core" },
      { phrase: "syllabus", weight: 2.5, type: "core" },

      // Technical terms - multi-word
      { phrase: "student services", weight: 2.0, type: "technical" },
      { phrase: "campus facilities", weight: 2.0, type: "technical" },
      { phrase: "library services", weight: 2.0, type: "technical" },
      { phrase: "IT support", weight: 2.0, type: "technical" },
      { phrase: "health services", weight: 2.0, type: "technical" },
      { phrase: "course schedule", weight: 2.0, type: "technical" },
      { phrase: "study resources", weight: 2.0, type: "technical" },
      { phrase: "academic support", weight: 2.0, type: "technical" },
      { phrase: "student center", weight: 2.0, type: "technical" },
      { phrase: "learning facilities", weight: 2.0, type: "technical" },
      { phrase: "medical services", weight: 2.0, type: "technical" },
      { phrase: "student insurance", weight: 2.0, type: "technical" },
      { phrase: "healthcare facilities", weight: 2.0, type: "technical" },
      { phrase: "mental health services", weight: 2.0, type: "technical" },
      { phrase: "counseling services", weight: 2.0, type: "technical" },

      // Technical terms - single words
      { phrase: "facilities", weight: 2.0, type: "technical" },
      { phrase: "services", weight: 2.0, type: "technical" },
      { phrase: "resources", weight: 2.0, type: "technical" },
      { phrase: "library", weight: 2.0, type: "technical" },
      { phrase: "support", weight: 2.0, type: "technical" },
      { phrase: "healthcare", weight: 2.0, type: "technical" },
      { phrase: "insurance", weight: 2.0, type: "technical" },

      // Context terms - multi-word
      { phrase: "student housing", weight: 1.5, type: "context" },
      { phrase: "accommodation options", weight: 1.5, type: "context" },
      { phrase: "housing rules", weight: 1.5, type: "context" },
      { phrase: "student transport", weight: 1.5, type: "context" },
      { phrase: "campus life", weight: 1.5, type: "context" },
      { phrase: "student welfare", weight: 1.5, type: "context" },
      { phrase: "study tips", weight: 1.5, type: "context" },
      { phrase: "campus map", weight: 1.5, type: "context" },
      { phrase: "class schedule", weight: 1.5, type: "context" },
      { phrase: "academic rules", weight: 1.5, type: "context" },
      { phrase: "medical support", weight: 1.5, type: "context" },
      { phrase: "health coverage", weight: 1.5, type: "context" },
      { phrase: "campus clinic", weight: 1.5, type: "context" },

      // Context terms - single words
      { phrase: "housing", weight: 1.5, type: "context" },
      { phrase: "accommodation", weight: 1.5, type: "context" },
      { phrase: "dormitory", weight: 1.5, type: "context" },
      { phrase: "transport", weight: 1.5, type: "context" },
      { phrase: "campus", weight: 1.5, type: "context" },
    ],
  },

  scholarships: {
    baseWeight: 2.5,
    requiresCoreMatch: true,
    keywords: [
      // Core terms - multi-word
      { phrase: "scholarship opportunities", weight: 3.5, type: "core" },
      { phrase: "scholarship program", weight: 3.5, type: "core" },
      { phrase: "financial aid", weight: 3.5, type: "core" },
      { phrase: "study grant", weight: 3.5, type: "core" },
      { phrase: "merit scholarship", weight: 3.5, type: "core" },
      { phrase: "full scholarship", weight: 3.5, type: "core" },
      { phrase: "international scholarship", weight: 3.5, type: "core" },
      { phrase: "government scholarships", weight: 3.5, type: "core" },
      { phrase: "university scholarships", weight: 3.5, type: "core" },
      { phrase: "research scholarships", weight: 3.5, type: "core" },
      { phrase: "academic scholarship", weight: 3.5, type: "core" },
      { phrase: "fully funded", weight: 3.5, type: "core" },
      { phrase: "partial scholarship", weight: 3.5, type: "core" },
      { phrase: "scholarship awards", weight: 3.5, type: "core" },

      // Core terms - single words
      { phrase: "scholarship", weight: 3.5, type: "core" },
      { phrase: "scholarships", weight: 3.5, type: "core" },
      { phrase: "grant", weight: 3.5, type: "core" },
      { phrase: "grants", weight: 3.5, type: "core" },
      { phrase: "funding", weight: 3.5, type: "core" },

      // Technical terms - multi-word
      { phrase: "tuition fees", weight: 2.5, type: "technical" },
      { phrase: "living expenses", weight: 2.5, type: "technical" },
      { phrase: "scholarship deadline", weight: 2.5, type: "technical" },
      { phrase: "scholarship eligibility", weight: 2.5, type: "technical" },
      {
        phrase: "scholarship academic requirements",
        weight: 2.5,
        type: "technical",
      },
      { phrase: "selection process", weight: 2.5, type: "technical" },
      { phrase: "living allowance", weight: 2.5, type: "technical" },
      { phrase: "travel allowance", weight: 2.5, type: "technical" },
      { phrase: "research funding", weight: 2.5, type: "technical" },
      { phrase: "monthly stipend", weight: 2.5, type: "technical" },

      // Technical terms - single words
      { phrase: "tuition", weight: 2.5, type: "technical" },
      { phrase: "stipend", weight: 2.5, type: "technical" },
      { phrase: "allowance", weight: 2.5, type: "technical" },
      { phrase: "funds", weight: 2.5, type: "technical" },
      { phrase: "expenses", weight: 2.5, type: "technical" },

      // Context terms - multi-word
      { phrase: "academic excellence", weight: 2.0, type: "context" },
      { phrase: "funding support", weight: 2.0, type: "context" },
      { phrase: "financial support", weight: 2.0, type: "context" },
      { phrase: "award value", weight: 2.0, type: "context" },
      { phrase: "academic merit", weight: 2.0, type: "context" },
      { phrase: "financial need", weight: 2.0, type: "context" },
      { phrase: "funding opportunity", weight: 2.0, type: "context" },
      { phrase: "financial assistance", weight: 2.0, type: "context" },
      { phrase: "merit based", weight: 2.0, type: "context" },
      { phrase: "need based", weight: 2.0, type: "context" },

      // Context terms - single words
      { phrase: "merit", weight: 2.0, type: "context" },
      { phrase: "award", weight: 2.0, type: "context" },
      { phrase: "aid", weight: 2.0, type: "context" },
      { phrase: "assistance", weight: 2.0, type: "context" },
      { phrase: "benefits", weight: 2.0, type: "context" },
    ],
  },

  "visa-guide": {
    baseWeight: 2.0,
    requiresCoreMatch: true,
    keywords: [
      // Core terms - multi-word
      { phrase: "visa process", weight: 2.5, type: "core" },
      { phrase: "visa application", weight: 2.5, type: "core" },
      { phrase: "student visa", weight: 2.5, type: "core" },
      { phrase: "visa requirements", weight: 2.5, type: "core" },
      { phrase: "visa guide", weight: 2.5, type: "core" },
      { phrase: "visa renewal", weight: 2.5, type: "core" },
      { phrase: "visa status", weight: 2.5, type: "core" },
      { phrase: "visa processing", weight: 2.5, type: "core" },
      { phrase: "visa approval", weight: 2.5, type: "core" },
      { phrase: "study visa", weight: 2.5, type: "core" },

      // Core terms - single words
      { phrase: "visa", weight: 2.5, type: "core" },
      { phrase: "visas", weight: 2.5, type: "core" },
      { phrase: "permit", weight: 2.5, type: "core" },
      { phrase: "permits", weight: 2.5, type: "core" },
      { phrase: "immigration", weight: 2.5, type: "core" },

      // Technical terms - multi-word
      { phrase: "visa required documents", weight: 2.0, type: "technical" },
      { phrase: "visa document checklist", weight: 2.0, type: "technical" },
      { phrase: "visa financial documents", weight: 2.0, type: "technical" },
      { phrase: "bank statements", weight: 2.0, type: "technical" },
      { phrase: "sponsor letter", weight: 2.0, type: "technical" },
      { phrase: "proof of funds", weight: 2.0, type: "technical" },
      { phrase: "visa fee", weight: 2.0, type: "technical" },
      { phrase: "visa processing time", weight: 2.0, type: "technical" },
      { phrase: "biometric appointment", weight: 2.0, type: "technical" },
      { phrase: "visa health insurance", weight: 2.0, type: "technical" },
      { phrase: "visa medical examination", weight: 2.0, type: "technical" },
      { phrase: "visa health certificate", weight: 2.0, type: "technical" },

      // Technical terms - single words
      { phrase: "documents", weight: 2.0, type: "technical" },
      { phrase: "passport", weight: 2.0, type: "technical" },
      { phrase: "biometrics", weight: 2.0, type: "technical" },
      { phrase: "embassy", weight: 2.0, type: "technical" },
      { phrase: "consulate", weight: 2.0, type: "technical" },

      // Context terms - multi-word
      { phrase: "visa interview", weight: 1.5, type: "context" },
      { phrase: "embassy appointment", weight: 1.5, type: "context" },
      { phrase: "arrival registration", weight: 1.5, type: "context" },
      { phrase: "police registration", weight: 1.5, type: "context" },
      { phrase: "visa office", weight: 1.5, type: "context" },
      { phrase: "visa center", weight: 1.5, type: "context" },
      { phrase: "entry clearance", weight: 1.5, type: "context" },
      { phrase: "border control", weight: 1.5, type: "context" },
      { phrase: "immigration rules", weight: 1.5, type: "context" },
      { phrase: "visa conditions", weight: 1.5, type: "context" },

      // Context terms - single words
      { phrase: "interview", weight: 1.5, type: "context" },
      { phrase: "appointment", weight: 1.5, type: "context" },
      { phrase: "registration", weight: 1.5, type: "context" },
      { phrase: "customs", weight: 1.5, type: "context" },
      { phrase: "border", weight: 1.5, type: "context" },
    ],
  },

  career: {
    baseWeight: 2.0,
    requiresCoreMatch: true,
    keywords: [
      // Core terms - multi-word
      { phrase: "career planning", weight: 2.5, type: "core" },
      { phrase: "job search", weight: 2.5, type: "core" },
      { phrase: "career options", weight: 2.5, type: "core" },
      { phrase: "industry research", weight: 2.5, type: "core" },
      { phrase: "career path", weight: 2.5, type: "core" },
      { phrase: "career development", weight: 2.5, type: "core" },
      { phrase: "career opportunities", weight: 2.5, type: "core" },
      { phrase: "job hunting", weight: 2.5, type: "core" },
      { phrase: "employment opportunities", weight: 2.5, type: "core" },
      { phrase: "career guidance", weight: 2.5, type: "core" },

      // Core terms - single words
      { phrase: "career", weight: 2.5, type: "core" },
      { phrase: "job", weight: 2.5, type: "core" },
      { phrase: "employment", weight: 2.5, type: "core" },
      { phrase: "profession", weight: 2.5, type: "core" },
      { phrase: "work", weight: 2.5, type: "core" },

      // Technical terms - multi-word
      { phrase: "job applications", weight: 2.0, type: "technical" },
      { phrase: "resume writing", weight: 2.0, type: "technical" },
      { phrase: "cover letter", weight: 2.0, type: "technical" },
      { phrase: "interview preparation", weight: 2.0, type: "technical" },
      { phrase: "job requirements", weight: 2.0, type: "technical" },
      { phrase: "CV preparation", weight: 2.0, type: "technical" },
      { phrase: "job description", weight: 2.0, type: "technical" },
      { phrase: "career work experience", weight: 2.0, type: "technical" },
      { phrase: "job placement", weight: 2.0, type: "technical" },
      { phrase: "career services", weight: 2.0, type: "technical" },

      // Technical terms - single words
      { phrase: "resume", weight: 2.0, type: "technical" },
      { phrase: "interview", weight: 2.0, type: "technical" },
      { phrase: "CV", weight: 2.0, type: "technical" },
      { phrase: "intern", weight: 2.0, type: "technical" },
      { phrase: "internship", weight: 2.0, type: "technical" },

      // Context terms - multi-word
      { phrase: "skill development", weight: 1.5, type: "context" },
      { phrase: "professional skills", weight: 1.5, type: "context" },
      { phrase: "professional networking", weight: 1.5, type: "context" },
      { phrase: "industry connections", weight: 1.5, type: "context" },
      { phrase: "soft skills", weight: 1.5, type: "context" },
      { phrase: "work environment", weight: 1.5, type: "context" },
      { phrase: "workplace culture", weight: 1.5, type: "context" },
      { phrase: "career fair", weight: 1.5, type: "context" },
      { phrase: "job market", weight: 1.5, type: "context" },
      { phrase: "professional development", weight: 1.5, type: "context" },

      // Context terms - single words
      { phrase: "skills", weight: 1.5, type: "context" },
      { phrase: "networking", weight: 1.5, type: "context" },
      { phrase: "professional", weight: 1.5, type: "context" },
      { phrase: "industry", weight: 1.5, type: "context" },
      { phrase: "workplace", weight: 1.5, type: "context" },
    ],
  },

  "student-stories": {
    baseWeight: 2.0,
    requiresCoreMatch: true,
    keywords: [
      // Core terms - multi-word phrases
      { phrase: "student experience", weight: 2.5, type: "core" },
      { phrase: "personal journey", weight: 2.5, type: "core" },
      { phrase: "study experience", weight: 2.5, type: "core" },
      { phrase: "life story", weight: 2.5, type: "core" },
      { phrase: "student life", weight: 2.5, type: "core" },
      { phrase: "my experience", weight: 2.5, type: "core" },
      { phrase: "personal story", weight: 2.5, type: "core" },
      { phrase: "student journey", weight: 2.5, type: "core" },
      { phrase: "student story", weight: 2.5, type: "core" },
      { phrase: "personal experience", weight: 2.5, type: "core" },

      // Core terms - single words
      { phrase: "experience", weight: 2.5, type: "core" },
      { phrase: "story", weight: 2.5, type: "core" },
      { phrase: "journey", weight: 2.5, type: "core" },
      { phrase: "testimonial", weight: 2.5, type: "core" },
      { phrase: "diary", weight: 2.5, type: "core" },

      // Technical terms - multi-word
      { phrase: "personal growth", weight: 2.0, type: "technical" },
      { phrase: "cultural adjustment", weight: 2.0, type: "technical" },
      { phrase: "learning experience", weight: 2.0, type: "technical" },
      { phrase: "social integration", weight: 2.0, type: "technical" },
      { phrase: "personal development", weight: 2.0, type: "technical" },
      { phrase: "cultural learning", weight: 2.0, type: "technical" },
      { phrase: "personal challenges", weight: 2.0, type: "technical" },
      { phrase: "personal achievements", weight: 2.0, type: "technical" },
      { phrase: "cultural discoveries", weight: 2.0, type: "technical" },

      // Context terms - multi-word
      { phrase: "personal insights", weight: 1.5, type: "context" },
      { phrase: "cultural observations", weight: 1.5, type: "context" },
      { phrase: "life lessons", weight: 1.5, type: "context" },
      { phrase: "personal discoveries", weight: 1.5, type: "context" },
      { phrase: "learning moments", weight: 1.5, type: "context" },
      { phrase: "student memory", weight: 1.5, type: "context" },
      { phrase: "personal milestones", weight: 1.5, type: "context" },
      { phrase: "friendship building", weight: 1.5, type: "context" },
      { phrase: "social life", weight: 1.5, type: "context" },
      { phrase: "community involvement", weight: 1.5, type: "context" },
      { phrase: "campus activities stories", weight: 1.5, type: "context" },
      { phrase: "student activities narrative", weight: 1.5, type: "context" },
      { phrase: "campus events experience", weight: 1.5, type: "context" },
      { phrase: "student community stories", weight: 1.5, type: "context" },
      { phrase: "making friends abroad", weight: 1.5, type: "context" },
      { phrase: "social events stories", weight: 1.5, type: "context" },
      { phrase: "daily life narrative", weight: 1.5, type: "context" },

      // Context terms - single words
      { phrase: "friends", weight: 1.5, type: "context" },
      { phrase: "community", weight: 1.5, type: "context" },
      { phrase: "activities", weight: 1.5, type: "context" },
      { phrase: "social", weight: 1.5, type: "context" },
      { phrase: "memories", weight: 1.5, type: "context" },
    ],
  },
};

export const tagWeights: Record<string, number> = {
  // Primary Decision Drivers (5.0-4.6)
  studyFields: 5.0,
  programLevels: 4.8,
  destinations: 4.6,

  // Critical Factors (4.5-4.0)
  financial: 4.5,
  requirements: 4.2,
  studyDelivery: 4.0,

  // Support & Future (3.5-2.5)
  career: 3.5,
  application: 3.0,
  healthcare: 2.5,

  // Supplementary (2.0-1.5)
  lifestyle: 1.5,
};
